import * as signalR from '@microsoft/signalr';
import { TicketDto, UrlKeyDto } from '@trucktrax/trucktrax-ts-common';
import config from 'react-global-configuration';
import { Subject } from 'rxjs';
import recordLog from '../../util/errorUtil';
import { store } from '../../Main';
import {
  removeIncomingTicket,
  removeTicket,
  syncIncomingTicket,
  syncTicket,
} from '../../store/actions/ticketActions';
import { fetchCurrentTicketForTruck, fetchCurrentTicketList, fetchTicket } from '../ticketsService';
import { TicketWithCurrentStatusDto } from '../../types';
import { JOIN_GROUP, RECEIVE_MESSAGE } from '../../constants/appConstants';
import {
  HTTP_BRIDGE_BASE_URL,
  CLOSE_INCOMING_TICKET_GROUP,
  CLOSE_TICKET_GROUP,
  SYNC_INCOMING_TICKET_GROUP,
  TICKETING_SOCKET,
  SYNC_TICKET_GROUP,
} from '../../constants/apiConstants';
import { getTokens, isExpired } from '../../util/authUtil';
import { ensureFreshAccessToken, RefreshOutcome } from '../requestService';
import { ConnectionState, isAuthFailureError, ReconnectScheduler } from '../../util/socketUtil';
import { allPromisesResolved } from '../../util/appUtil';

export class TicketingSocket {
  private connectionState: ConnectionState;

  private shouldReconnectOnClose: boolean = true;

  private hubConnection: signalR.HubConnection | null;

  private currentRegion?: UrlKeyDto;

  private selectedProductLine?: string;

  // Server-side state: whether the current SignalR connection has the IM
  // destination-region groups joined. Cleared on any disconnect because the
  // ConnectionId — and therefore its group memberships — go with it.
  incomingGroupsJoined = false;

  // Reporting flag: while true, IM ticket sync/close events are dispatched
  // to the store and the socket auto-joins the IM destination-region groups
  // on every (re)connect. Driven app-wide by IncomingMaterialsSubscription
  // (DEV-4154) — on while the ViewIncomingMaterials flag is enabled for the
  // current region, off when the flag drops or the user leaves GeoTrax —
  // without tearing down the hub.
  // Survives disconnect/connect cycles (incl. region changes via
  // ticketSocketInit, which tear the hub down and rebuild it) so the new
  // connection auto-joins for whatever region we land on.
  reportIncomingMaterialTickets = false;

  tertiaryDataChange$ = new Subject<TicketDto>();

  /**
   * Owns backoff state (attempt counter + pending timer) for this socket.
   * Kept private so the reconnect contract is honored through {@link reconnect}
   * and {@link disconnect} rather than by callers reaching in directly.
   */
  private reconnectScheduler = new ReconnectScheduler();

  constructor() {
    this.connectionState = ConnectionState.Disconnected;
    this.hubConnection = null;
  }

  onResponse = async (response: string, selectedProductLine?: string) => {
    const { message: socketTicket, group } = JSON.parse(response);

    if (socketTicket.hasTertiaryDataUpdate) {
      this.tertiaryDataChange$.next(socketTicket);
      return;
    }

    // Incoming Materials events take a different shape (TicketWithCurrentStatusDto)
    // and target a different slice. They also bypass the source-side
    // plantProductLine guard below — the IM page does not pin
    // selectedProductLine to ReadyMix, so checking it here would drop
    // legitimate IM updates.
    if (group === CLOSE_INCOMING_TICKET_GROUP || group === SYNC_INCOMING_TICKET_GROUP) {
      // The FE doesn't actively leave these groups when reporting turns off —
      // the BE hub has no LeaveGroup endpoint, and server-side cleanup happens
      // for free on the next disconnect (region change / logout / dropped
      // connection). For the only gap (reporting disabled on a live
      // connection, e.g. leaving GeoTrax for Admin in the same region), we
      // filter here so stale events don't keep piling into the slice.
      if (!this.reportIncomingMaterialTickets) return;
      if (group === CLOSE_INCOMING_TICKET_GROUP) {
        // Close removes by id straight from the websocket payload: the BE
        // may have hard-deleted the ticket between emitting close and any
        // refetch, so we can't depend on it still being retrievable.
        store.dispatch(removeIncomingTicket(socketTicket.id));
        return;
      }
      // BE populates currentStatus on the wire payload via AppendStatus
      // before broadcasting (TicketsService.SyncExternal, post-DEV-3875),
      // so we can dispatch the socket payload directly without a refetch.
      store.dispatch(syncIncomingTicket(socketTicket as TicketWithCurrentStatusDto));
      return;
    }

    const ticket = await fetchTicket(socketTicket.id);
    if (!ticket || ticket?.externalInfo?.plantProductLine !== selectedProductLine) {
      return;
    }
    if (group === SYNC_TICKET_GROUP) {
      store.dispatch(syncTicket(ticket));
    } else if (group === CLOSE_TICKET_GROUP) {
      store.dispatch(removeTicket(ticket));
      const nextTicket = await fetchCurrentTicketForTruck(
        ticket.truck?.url!,
        this.currentRegion?.url,
        selectedProductLine,
      );
      if (nextTicket) {
        store.dispatch(syncTicket(nextTicket));
      }
    }
  };

  async connect(currentRegion?: UrlKeyDto, selectedProductLine?: string) {
    const { tenantId: tenant } = store.getState();
    let { accessToken } = getTokens();

    // do not try to connect if we're not logged in
    if (!accessToken) {
      return;
    }

    // An expired token 403s on /negotiate and spins the reconnect loop, so
    // refresh first. transient -> keep backing off; invalid -> user logged out.
    if (isExpired(accessToken)) {
      const refreshOutcome = await ensureFreshAccessToken();
      switch (refreshOutcome) {
        case RefreshOutcome.Refreshed:
          break;
        case RefreshOutcome.Transient:
          this.reconnect();
          return;
        case RefreshOutcome.Invalid:
          return;
        default:
          return;
      }
      accessToken = getTokens().accessToken;
      if (!accessToken) {
        return;
      }
    }

    const isRedundantConnectionAttempt =
      this.connectionState === ConnectionState.Connected ||
      this.connectionState === ConnectionState.Connecting;
    if (isRedundantConnectionAttempt) {
      return;
    }
    if (!currentRegion?.url) {
      return;
    }
    this.connectionState = ConnectionState.Connecting;
    this.currentRegion = currentRegion;
    this.selectedProductLine = selectedProductLine;

    const regionUrl = this.currentRegion!.url!.trim();
    const TICKETING_SOCKET_URL = `${config.get(HTTP_BRIDGE_BASE_URL)}${TICKETING_SOCKET}`;

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${TICKETING_SOCKET_URL}?tk=${accessToken}`, {
        accessTokenFactory: () => accessToken,
      })
      .configureLogging(signalR.LogLevel.None)
      .build();

    this.hubConnection.serverTimeoutInMilliseconds = 30_000;
    this.hubConnection.on(RECEIVE_MESSAGE, message => {
      this.onResponse(message, selectedProductLine);
    });
    const localHubConnection = this.hubConnection;
    this.shouldReconnectOnClose = true;

    this.hubConnection.onclose(_ => {
      // Mark the connection as down but preserve currentRegion/selectedProductLine
      // so the backoff-driven reconnect can resume against the same hub. A full
      // teardown (destroyHub) is only requested on region change or logout and
      // goes through disconnect(true) explicitly.
      const matchingConnection = this.hubConnection === localHubConnection;
      if (!matchingConnection) {
        return;
      }

      this.clearHubConnectionState(true);
      if (this.shouldReconnectOnClose) {
        this.reconnect();
      }
    });

    try {
      await localHubConnection.start();
      // TODO: Remove tenant from websocket group (work is done in future card)
      const groupJoinResponses = await Promise.allSettled(
        [SYNC_TICKET_GROUP, CLOSE_TICKET_GROUP]
          .map(group => `${group}${tenant}/${regionUrl}`)
          .map(group => localHubConnection.invoke(JOIN_GROUP, group)),
      );

      const allGroupsConnected = allPromisesResolved(groupJoinResponses);
      if (!allGroupsConnected) {
        throw new Error('Failed to establish all group connections forcing reconnect');
      }

      const matchingConnection = this.hubConnection === localHubConnection;
      if (matchingConnection) {
        this.connectionState = ConnectionState.Connected;
        // A full handshake plus group-join cycle succeeded, so reset backoff.
        // If the server later drops us we want the first recovery attempt to be
        // quick rather than inheriting stale delay from an earlier outage.
        this.reconnectScheduler.reset();
        // retrieving all tickets after connection to account for new tickets received
        // from app refresh to websocket connection success
        store.dispatch<any>(fetchCurrentTicketList(this.currentRegion!.url, selectedProductLine));
        // If IM reporting is on, (re-)join the destination-region groups for
        // whatever region this connection just established. Triggered both on
        // initial connect-while-reporting and on every reconnect (onclose path)
        // and region change (ticketSocketInit path).
        if (this.reportIncomingMaterialTickets) {
          this.incomingGroupsJoined = false;
          await this.joinIncomingMaterialsGroups();
        }
      }
    } catch (err) {
      recordLog('warn', 'Ticketing Socket', err);
      const matchingConnection = this.hubConnection === localHubConnection;
      if (matchingConnection) {
        console.error('[issue] TicketingSocket error connecting', err);
        this.clearHubConnectionState(true);
        // A 401/403 here is the server refusing a token the client thinks is
        // valid (e.g. auth outage rejecting good tokens, DEV-4179): refresh,
        // then retry -- unless the session is definitively gone.
        if (isAuthFailureError(err)) {
          const refreshOutcome = await ensureFreshAccessToken();
          if (refreshOutcome === RefreshOutcome.Invalid) {
            return;
          }
        }
        this.reconnect();
      }
    }
  }

  /**
   * Turns IM ticket reporting on or off. Driven app-wide by
   * IncomingMaterialsSubscription's flag/region lifecycle (DEV-4154).
   *
   * The flag survives disconnect/connect cycles (incl. region-change-driven
   * destroyHub) so the next successful connect auto-joins for whatever region
   * we land on. On disable we don't try to release the groups server-side:
   * the WebSocketHub doesn't expose LeaveGroup, and SignalR cleans up group
   * memberships when the ConnectionId drops (region change, logout, dropped
   * connection). The onResponse guard against reportIncomingMaterialTickets
   * handles the only gap — reporting disabled on a live connection (e.g.
   * leaving GeoTrax for Admin), where the BE keeps emitting but the FE
   * should ignore.
   */
  async setReportIncomingMaterialTickets(enabled: boolean) {
    this.reportIncomingMaterialTickets = enabled;
    if (!enabled) return;
    try {
      await this.joinIncomingMaterialsGroups();
    } catch (err) {
      // Bounce the connection so the next connect()'s success path can
      // re-issue the joins — cheaper and more reliable than open-coding a
      // retry inside joinIncomingMaterialsGroups. Same recovery path the
      // hub uses for any transport-level failure.
      recordLog('warn', 'Ticketing Socket — join IM groups', err);
      await this.disconnect();
      this.reconnect();
    }
  }

  /**
   * Subscribe to the Incoming Materials ticket sync/close groups scoped to the
   * current region (as the destination). Idempotent — repeated calls without
   * an intervening disconnect are no-ops. When called offline this is a
   * harmless no-op: the rejoin is driven by reportIncomingMaterialTickets on
   * connect()'s success path.
   *
   * Throws on JoinGroup failure so callers can decide how to recover:
   * connect()'s outer catch already routes failures into reconnect(), and
   * setReportIncomingMaterialTickets bounces the connection itself.
   */
  async joinIncomingMaterialsGroups() {
    if (this.incomingGroupsJoined) return;
    if (
      this.connectionState !== ConnectionState.Connected ||
      !this.hubConnection ||
      !this.currentRegion?.url
    )
      return;

    const { tenantId: tenant } = store.getState();
    const regionUrl = this.currentRegion.url.trim();
    const incomingGroupsJoined = await Promise.allSettled(
      [SYNC_INCOMING_TICKET_GROUP, CLOSE_INCOMING_TICKET_GROUP]
        .map(group => `${group}${tenant}/${regionUrl}`)
        .map(group => this.hubConnection!.invoke(JOIN_GROUP, group)),
    );
    const allGroupsConnected = allPromisesResolved(incomingGroupsJoined);
    if (!allGroupsConnected) {
      throw new Error(
        'Failed to establish all group connections for Incoming Materials forcing a reconnect',
      );
    }

    // Flip the server-side flag only after the JoinGroup invokes succeed.
    // Setting it earlier (before the connection guard, or before invoke
    // resolves) lets an offline or failed attempt poison future joins by
    // making the idempotency check short-circuit even though no
    // subscription exists.
    this.incomingGroupsJoined = true;
  }

  /**
   * Queues the next reconnect attempt using capped exponential backoff.
   * The scheduler handles the timer and attempt bookkeeping; this method
   * only supplies the connect arguments captured when the socket was opened.
   */
  private reconnect(): void {
    // Never reconnect over a live/opening socket -- a connect() that lands
    // Connected during the 403-catch refresh await must not be orphaned (PR #1217).
    if (
      this.connectionState === ConnectionState.Connecting ||
      this.connectionState === ConnectionState.Connected
    ) {
      return;
    }

    if (
      this.connectionState === ConnectionState.Reconnecting &&
      this.reconnectScheduler.isPending
    ) {
      return;
    }

    this.connectionState = ConnectionState.Reconnecting;
    this.reconnectScheduler.schedule(async () => {
      const { currentRegion, selectedProductLine } = store.getState();
      if (this.reconnectScheduler.currentAttempt >= 2) {
        // Rebuild the (possibly wedged) hub but preserve backoff -- resetting
        // it here pins persistent failures at a 2-4s retry storm (DEV-4179).
        await this.disconnect(true, { preserveBackoff: true });
        await this.connect(currentRegion, selectedProductLine);
        return;
      }
      await this.connect(currentRegion, selectedProductLine);
    });
  }

  async disconnect(destroyHub?: boolean, { preserveBackoff = false } = {}): Promise<void> {
    this.shouldReconnectOnClose = false;
    if (!preserveBackoff) {
      this.reconnectScheduler.reset();
    }

    try {
      if (this.hubConnection) {
        await this.hubConnection.stop();
      }
    } catch (_: unknown) {
      // Suppress errors from stop()
    } finally {
      this.clearHubConnectionState(destroyHub);
      this.shouldReconnectOnClose = true;
    }
  }

  private clearHubConnectionState(destroyHub: boolean = false): void {
    if (this.connectionState !== ConnectionState.Disconnected) {
      this.connectionState = ConnectionState.Disconnected;
    }

    if (destroyHub && this.hubConnection) {
      this.hubConnection.off(RECEIVE_MESSAGE);
      this.hubConnection = null;
    }

    this.incomingGroupsJoined = false;
    this.currentRegion = undefined;
    this.selectedProductLine = undefined;
  }

  public async attemptConnectionChange(region?: UrlKeyDto, productLine?: string): Promise<void> {
    if (!region) {
      return;
    }

    if (
      this.currentRegion?.url === region.url &&
      this.selectedProductLine === productLine &&
      this.connectionState === ConnectionState.Connected
    ) {
      return;
    }

    await this.disconnect(true);
    await this.connect(region, productLine);
  }
}

export default new TicketingSocket();
